import React, { useEffect, useRef } from 'react';

/**
 * FocusGuard — programmatically moves focus to the first focusable element
 * or a designated landmark when a route changes.
 *
 * Focus is only moved on SPA route transitions, never on the initial page
 * load: on first load focus must stay at the top of the document so the
 * skip link is the first Tab stop (WCAG 2.4.1 / 2.4.3).
 *
 * Usage: place one near the top of each page/route.
 *   <FocusGuard landmarkId="main-content" />
 */
interface FocusGuardProps {
  /** id of the element to receive focus */
  landmarkId?: string;
  /** optionally focus the first focusable element inside the landmark */
  autoFocusFirst?: boolean;
}

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

// Module-level: false until the first FocusGuard has mounted once.
// The first mount is the initial page load — focus must not move then.
let hasMountedOnce = false;

const FocusGuard: React.FC<FocusGuardProps> = ({ landmarkId = 'main-content', autoFocusFirst = true }) => {
  const processed = useRef(false);

  useEffect(() => {
    if (!hasMountedOnce) {
      hasMountedOnce = true;
      return;
    }

    processed.current = false;

    const timer = requestAnimationFrame(() => {
      if (processed.current) return;
      processed.current = true;

      const landmark = document.getElementById(landmarkId);
      if (!landmark) return;

      if (autoFocusFirst) {
        const firstFocusable = landmark.querySelector(FOCUSABLE_SELECTORS.join(', '));
        if (firstFocusable) {
          (firstFocusable as HTMLElement).focus();
          return;
        }
      }

      // tabindex="-1" makes the landmark programmatically focusable without
      // adding it to the sequential tab order — and it must stay that way.
      if (!landmark.hasAttribute('tabindex')) {
        landmark.setAttribute('tabindex', '-1');
      }
      landmark.focus();
    });

    return () => cancelAnimationFrame(timer);
  }, [landmarkId, autoFocusFirst]);

  return null;
};

export default FocusGuard;
